import type { Filter, SortRule } from "@smartsuite/types";

export interface DocumentDesignState {
  items: DocumentDesignItem[];
}

export enum DocumentDesignItemType {
  circle = "circle",
  field = "field",
  file = "file",
  line = "line",
  linkedRecord = "linkedRecord",
  multipleSelect = "multipleSelect",
  rect = "rect",
  text = "text",
  link = "link",
  image = "image",
  signature = "signature",
  textarea = "textarea",
}

export enum FieldViewModes {
  table = "table",
  list = "list",
  single = "single",
}

export type DocumentDesignItem =
  | FieldItem
  | RectItem
  | CircleItem
  | LineItem
  | TextItem
  | LinkItem
  | LinkedRecordItem
  | MultipleSelectItem
  | FileItem
  | ImageItem
  | SignatureItem
  | TextareaFieldItem;

export function isItemOfType<TType extends DocumentDesignItemType>(
  item: DocumentDesignItem,
  type: TType,
): item is Extract<DocumentDesignItem, { type: TType }> {
  return item.type === type;
}

export type DocumentDesignItemPosition = "absolute" | "relative" | "anchored";

export interface BaseItem extends Scale, Coords {
  id: string;
  type: DocumentDesignItemType;
  angle?: number;
  stroke?: string;
  strokeWidth?: number;
  fill?: string;
  active?: boolean;

  position?: DocumentDesignItemPosition;
  anchorId?: string;
  anchorDistance?: number;
  hideWhenEmpty?: boolean;
}

export type AddressFieldType = "plain-text" | "link" | "map";

export interface TextareaFieldItem extends BaseItem, Dimensions, TextAttrs {
  type: DocumentDesignItemType.textarea;
  field_slug: string;
}

export interface FieldItem extends BaseItem, Dimensions, TextAttrs {
  type: DocumentDesignItemType.field;
  field_slug: string;
  addressType?: AddressFieldType;
}

export interface RectItem extends BaseItem, Dimensions {
  type: DocumentDesignItemType.rect;
}

export interface CircleItem extends BaseItem, Dimensions {
  type: DocumentDesignItemType.circle;
  radius: number;
}

export interface LineItem extends BaseItem, Dimensions {
  type: DocumentDesignItemType.line;
}

export interface TextItem extends BaseItem, Dimensions, TextAttrs {
  type: DocumentDesignItemType.text;
  template: string;
}

export interface LinkItem extends BaseItem, Dimensions, TextAttrs {
  type: DocumentDesignItemType.link;
  template: string;
  url: string;
}

export function isLinkItem(item: DocumentDesignItem): item is LinkItem {
  return item.type === DocumentDesignItemType.link;
}

export type ColumnAlignment = "left" | "center" | "right";

export interface LinkedRecordTableField {
  field: string;
  width: number;
  textAlign?: ColumnAlignment;
}

export interface LinkedRecordItem extends BaseItem, Dimensions, TextAttrs {
  type: DocumentDesignItemType.linkedRecord;
  field_slug: string;
  field_list: string[] | LinkedRecordTableField[];
  field_linked_application: string;
  sort?: SortRule[];
  filter?: Filter;
  mode: FieldViewModes;
}

export type LinkedRecordItemFieldList = LinkedRecordItem["field_list"];

export interface MultipleSelectItem extends BaseItem, Dimensions, TextAttrs {
  type: DocumentDesignItemType.multipleSelect;
  field_slug: string;
  mode: FieldViewModes;
}

export type FileItemDisplayMode = "first-image" | "all-images";
export type FileItemFitMode = "fit" | "fill" | "stretch";
export type FileItemSizingMode = "square" | "fixed-height" | "fixed-width";

export interface FileItem extends BaseItem {
  type: DocumentDesignItemType.file;
  field_slug: string;
  width?: number;
  height?: number;
  displayMode?: FileItemDisplayMode;
  fitMode?: FileItemFitMode;
  sizingMode?: FileItemSizingMode;
  imageSize?: number;
  imagePadding?: number;
}

export interface ImageItem extends BaseItem {
  type: DocumentDesignItemType.image;
  width?: number;
  height?: number;
  url?: string;
  handle?: string | null;
}

export interface SignatureItem extends BaseItem {
  type: DocumentDesignItemType.signature;
  field_slug: string;
  width?: number;
  height?: number;
}

// re-usable types

export interface Scale {
  scaleX?: number;
  scaleY?: number;
}

export interface Coords {
  top: number;
  left: number;
}

export interface Dimensions {
  width: number;
  height: number;
}

export interface TextAttrs {
  fontSize: number;
  fontFamily: string;
  fontWeight: string | number;
  lineHeight: number;
  textAlign: string;
}

export interface FieldAddress {
  location_address: string;
  location_address2: string;
  location_city: string;
  location_state: string;
  location_zip: string;
  location_country: string;
  location_longitude: string;
  location_latitude: string;
  sys_root: string;
}
